"use client";

/**
 * ShopLayoutShell — client-side composition of the storefront chrome.
 *
 * The Next.js server layout (`layout.tsx`) does the data fetches (session,
 * initial cart, etc.) and hands the derived nav state to this client shell.
 * Why client: `<ToastProvider>` uses `useState` / portals, so it MUST run on
 * the client. Wrapping it here keeps a single client-boundary tree around
 * the entire shop route group.
 *
 * `<CartProvider>` is already a client component upstream from this shell —
 * its existing instance in `layout.tsx` wraps `ShopLayoutShell`, so any cart
 * mutation triggered inside the shell (via QtyAndAdd / CartLineItem) reads
 * the same context.
 */
import type { ReactNode } from "react";
import {
  StorefrontFooter,
  ToastProvider,
  type StorefrontHeaderProps,
} from "@wmart/ui";
import { HeaderWithLiveCart } from "@/components/HeaderWithLiveCart";
import { LegalConsentGate } from "@/components/legal/LegalConsentGate";

interface ShopLayoutShellProps {
  headerProps: StorefrontHeaderProps;
  footerNavItems: { label: string; href: string }[];
  /** Translated footer chrome labels resolved server-side via next-intl. */
  footerLabels?: { navLabel?: string; copyright?: string };
  hasCustomerSession: boolean;
  /** Whether the current visitor is an approved buyer (for LocationChip gating). */
  isApproved?: boolean;
  children: ReactNode;
}

export function ShopLayoutShell({
  headerProps,
  footerNavItems,
  footerLabels,
  hasCustomerSession,
  isApproved = false,
  children,
}: ShopLayoutShellProps) {
  return (
    <ToastProvider>
      <div className="flex min-h-screen flex-col">
        {/* `relative z-40` promotes the entire header into its own stacking
            context above <main>. Without it, header descendants positioned
            absolutely (HeaderSearch popover, future account menus) compete
            with page content at the root stacking context and lose to later
            DOM siblings — most visibly the home-page hero image. */}
        <div className="relative z-40">
          <HeaderWithLiveCart {...headerProps} isApproved={isApproved} />
        </div>
        <main
          id="main"
          className="mx-auto w-full max-w-7xl flex-1 px-6 py-10"
        >
          {children}
        </main>
        <StorefrontFooter navItems={footerNavItems} {...footerLabels} />
      </div>
      <LegalConsentGate hasCustomerSession={hasCustomerSession} />
    </ToastProvider>
  );
}
